import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';

const RADIUS = {
	input: '8px',
	mid: '10px',
	card: '15px',
};

export const StyledFundraiserForm = styled(Box)(({ theme }) => ({
	'& .HeadingBox': {
		'& .MuiTypography-root': {
			textAlign: 'center',
		},
		padding: '48px 0 28px',
		margin: 'auto',
		width: '54%',

		[theme.breakpoints.down('md')]: {
			width: '100%',
			padding: '28px 20px 20px',
		},

		'& .Heading': {
			fontWeight: 'bold',
			marginBottom: '0.35em',
		},
	},

	'& .FundraiserFormBox': {
		width: '84%',
		margin: 'auto',
		border: `1px solid ${theme.palette.neutral.light}`,
		borderRadius: RADIUS.card,
		backgroundColor: '#fff',

		[theme.breakpoints.down('md')]: {
			width: '100%',
			borderRadius: RADIUS.mid,
		},

		// --- Tabs: centered pill group, no longer stretching edge-to-edge ---
		'& .FundraiserTabs': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			padding: '24px 40px',
			backgroundColor: '#F8F8F8',
			borderBottom: `1px solid ${theme.palette.neutral.light}`,
			borderTopLeftRadius: RADIUS.card,
			borderTopRightRadius: RADIUS.card,

			'& .StepItem': {
				display: 'flex',
				alignItems: 'center',
				gap: '10px',
				cursor: 'pointer',

				'& .StepCircle': {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					width: '28px',
					height: '28px',
					borderRadius: '50%',
					fontSize: '13px',
					fontWeight: 700,
					border: `2px solid ${theme.palette.neutral.light}`,
					color: theme.palette.text.secondary,
				},
				'& .StepLabel': {
					fontSize: '14px',
					fontWeight: 700,
					color: theme.palette.text.secondary,
				},

				'&.active .StepCircle, &.completed .StepCircle': {
					borderColor: theme.palette.primary.main,
					backgroundColor: theme.palette.primary.main,
					color: '#fff',
				},
				'&.active .StepLabel, &.completed .StepLabel': {
					color: theme.palette.text.primary,
				},
				'&.upcoming': {
					cursor: 'default',
					pointerEvents: 'none',
				},
			},

			'& .StepLine': {
				flex: 'unset', // was flex: 1, which stretched to fill all remaining horizontal space
				width: '48px', // fixed short connector instead
				height: '2px',
				backgroundColor: theme.palette.neutral.light,
				margin: '0 12px',
				'&.filled': {
					backgroundColor: theme.palette.primary.main,
				},
			},

			[theme.breakpoints.down('md')]: {
				padding: '16px 12px',
				'& .StepLabel': { display: 'none' },
				'& .StepLine': {
					width: '28px',
					margin: '0 8px',
				},
			},
		},

		'& .TabsContent': {
			padding: '40px',

			[theme.breakpoints.down('md')]: {
				padding: '20px',
			},

			'& .MuiTabPanel-root': {
				padding: 0,
			},

			// ---------------- Basic Info ----------------
			'& .BasicInfoTab': {
				'& .MuiOutlinedInput-root': {
					borderRadius: RADIUS.input,
				},

				'& .FundraiserTypeLabel': {
					color: 'black',
					fontWeight: 700,
					fontSize: '16px',
				},

				'& .FundraiserTypeStack': {
					marginTop: '14px',
					gap: '16px !important',
				},

				'& .TypeOption': {
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'start',
					border: `1.5px solid ${theme.palette.neutral.light}`,
					borderRadius: RADIUS.card,
					padding: '18px 20px 0px',
					flex: 1,
					cursor: 'pointer',
					position: 'relative', // needed for the absolutely-positioned check circle, same as ProductRow
					transition: 'border-color .15s ease, box-shadow .15s ease, background-color .15s ease',
					gap: '8px',

					'&:hover': {
						borderColor: alpha(theme.palette.primary.main, 0.5),
					},

					'&.selected': {
						borderColor: theme.palette.primary.main,
						borderWidth: '2px',
						backgroundColor: alpha(theme.palette.primary.main, 0.04),
						boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
					},

					'& .TypeOptionTitle': {
						fontWeight: 700,
						fontSize: '14.5px', // matches ProductName exactly
					},

					'& .DescItem': {
						fontSize: '12.5px', // matches ProductRate exactly
						color: theme.palette.text.secondary,
					},

					'& .HiddenRadio': {
						position: 'absolute',
						opacity: 0,
						width: 0,
						height: 0,
						padding: 0,
						overflow: 'hidden',
					},

					'& .SelectedCheckCircle': {
						position: 'absolute',
						top: '10px',
						right: '10px',
						width: '22px',
						height: '22px',
						borderRadius: '50%',
						backgroundColor: theme.palette.primary.main,
						color: '#fff',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: '14px',
					},
				},

				'& .KeepPagePrivateStack': {
					flexDirection: 'row',
					alignItems: 'center',
					marginTop: '8px',

					'& .MuiFormGroup-root': {
						marginBottom: 0,
					},

					'& .MuiFormControlLabel-root': {
						marginLeft: '-9px', // offsets Checkbox's built-in padding so label aligns flush left with other fields
						marginRight: '4px',
						marginBottom: 0,
					},

					'& .MuiTypography-root': {
						fontSize: '14px',
					},

					'& .MuiIconButton-root': {
						padding: '6px', // matches Checkbox's effective click-target height so the info icon centers with the checkbox+label row
					},
				},
				[theme.breakpoints.down('md')]: {
					'& .TypeOption': {
						padding: '18px 20px',
					},
				},
			},

			// ---------------- Choose Your Cause ----------------
			'& .ChooseCauseTab': {
				'& .ChooseCauseLabel': {
					fontSize: '16px',
					fontWeight: 700,
				},

				'& .ProductGrid': {
					display: 'grid',
					gridTemplateColumns: 'repeat(3, 1fr)',
					gap: '16px',

					[theme.breakpoints.down('md')]: {
						gridTemplateColumns: 'repeat(2, 1fr)',
					},

					[theme.breakpoints.down('sm')]: {
						gridTemplateColumns: '1fr',
					},
				},

				'& .ProductRow': {
					border: `1.5px solid ${theme.palette.neutral.light}`,
					borderRadius: RADIUS.card,
					padding: '18px 20px',
					backgroundColor: '#fff',
					transition: 'border-color .15s ease, box-shadow .15s ease, background-color .15s ease',
					position: 'relative',
					userSelect: 'none',
					'&:hover': {
						borderColor: alpha(theme.palette.primary.main, 0.5),
					},

					'&.selected': {
						borderColor: theme.palette.primary.main,
						borderWidth: '2px',
						backgroundColor: alpha(theme.palette.primary.main, 0.04),
						boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
					},

					'& .ProductName': {
						fontWeight: 700,
						fontSize: '14.5px',
					},

					'& .ProductRate': {
						color: theme.palette.text.secondary,
						fontSize: '12.5px',
					},
					'& .SelectedCheckCircle': {
						position: 'absolute',
						top: '10px',
						right: '10px',
						width: '22px',
						height: '22px',
						borderRadius: '50%',
						backgroundColor: theme.palette.primary.main,
						color: '#fff',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: '14px',
					},
				},
				'& .QuantityBox': {
					border: `1px solid ${theme.palette.neutral.light}`,
					borderRadius: RADIUS.mid,
					padding: '18px 20px',

					'& .QuantitySummary': {
						color: theme.palette.text.secondary,
						fontSize: '13px',
						marginTop: '4px',
					},

					'& .MuiIconButton-root': {
						border: `1px solid ${theme.palette.neutral.light}`,
						borderRadius: RADIUS.input,
						padding: '4px',
					},
				},

				'& .MuiOutlinedInput-root': {
					borderRadius: RADIUS.input,
				},
			},
		},

		// ---------------- Tell a Story ----------------
		'& .TellaStoryTab': {
			'& .CampaignOccasiong .MuiAutocomplete-root': {
				flexBasis: '50%',

				'& .MuiOutlinedInput-root': {
					borderRadius: RADIUS.input,
				},
			},

			'& .FundraiserCoverImage': {
				width: '100%',
				minHeight: '200px',
				borderRadius: RADIUS.card,
				objectFit: 'cover',
			},

			'& .FundraiserImageLoader': {
				minHeight: '200px',
				width: '100%',
				justifyContent: 'center',
				alignItems: 'center',
				borderRadius: RADIUS.card,
				backgroundColor: alpha(theme.palette.neutral.light, 0.5),
				'& img': {
					width: '50px',
				},
			},

			'& .FundraiserCoverButtons': {
				'& .MuiButton-root': {
					flexBasis: '50%',
					border: `1px solid ${theme.palette.neutral.light}`,
					borderRadius: RADIUS.mid,
					fontSize: '16px',

					'& svg': {
						marginBottom: '8px',
						marginLeft: '5px',
					},
				},
			},

			'& .reactEasyCrop_Container': {
				height: '450px',
				position: 'unset',
				borderRadius: RADIUS.card,
				overflow: 'hidden',

				'& img': {
					position: 'unset',
					width: '100%',
					height: '100%',
					objectFit: 'cover',
				},

				'& .reactEasyCrop_CropArea': {
					position: 'unset',
					transform: 'unset',
					boxShadow: 'none',
				},
			},
		},
	},
}));
